'use client';

import { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useUpdateBusinessDescriptionMutation } from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { useInvalidateQuery } from '@/hooks/useInvalidateQuery';
import { motion } from 'framer-motion';
import { formatBusinessDescription } from '@/lib/utils';

interface CampaignBusinessDescriptionProps {
  title: string;
  businessDescription: string;
  businessUrl: string;
  onNext: () => void;
  setBusinessDescription: (description: string) => void;
  setStepValid: (valid: boolean) => void;
  showInternalContinueButton?: boolean;
}

export interface CampaignBusinessDescriptionRef {
  approveDescription: () => Promise<boolean>;
}

export const CampaignBusinessDescription = forwardRef<CampaignBusinessDescriptionRef, CampaignBusinessDescriptionProps>(({
  businessDescription,
  businessUrl,
  setBusinessDescription,
  onNext,
  setStepValid,
  showInternalContinueButton = true
}, ref) => {
  const posthog = usePostHog();
  const [description, setDescription] = useState<string>(formatBusinessDescription(businessDescription));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const updateBusinessMutation = useUpdateBusinessDescriptionMutation({ businessUrl, description });
  const invalidateQuery = useInvalidateQuery();
  const { toast } = useToast();
  const originalDescriptionRef = useRef(businessDescription);
  const hasTrackedEdit = useRef(false);

  const approveDescription = async (): Promise<boolean> => {
    setBusinessDescription(description);

    // Track description approval
    posthog?.capture('onboarding_business_description_approved', {
      description_length: description.length,
      was_edited: description !== originalDescriptionRef.current,
      original_length: originalDescriptionRef.current?.length || 0,
      business_url: businessUrl,
    });

    try {
      await updateBusinessMutation.mutateAsync();
      invalidateQuery(['businesses', 'description-update', businessUrl]);
      return true;
    } catch (error) {
      toast({ description: 'Failed to update business description', variant: 'destructive' });

      posthog?.capture('onboarding_business_description_update_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        business_url: businessUrl,
      });

      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    approveDescription
  }));

  // Always allow continuation - no user action required - fix infinite loop by removing setStepValid from dependencies
  useEffect(() => {
    setStepValid(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const newDescription = e.target.value;
    setDescription(newDescription);

    // Track first edit if the description differs from the original
    if (!hasTrackedEdit.current && newDescription !== originalDescriptionRef.current) {
      posthog?.capture('onboarding_business_description_edited', {
        original_length: originalDescriptionRef.current?.length || 0,
        new_length: newDescription.length,
        business_url: businessUrl,
      });
      hasTrackedEdit.current = true;
    }
  };

  const handleContinue = async () => {
    const success = await approveDescription();
    if (success) {
      onNext();
    }
  };

  return (
    <motion.div 
      className='campaign-goal-generate flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <motion.p 
        className='text-base text-slate-900 mb-4'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        Please <strong>review and edit</strong> the following summary to ensure you are happy with it. It will be used to generate keywords for your <strong>Prospecting</strong> campaign.
      </motion.p>
      <motion.div 
        className='space-y-4'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        onAnimationComplete={() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
          }
        }}
      >
        <Card>
          <Textarea
            ref={textareaRef}
            name={'description'}
            className='h-[330px] p-4 !text-base leading-relaxed text-slate-900'
            defaultValue={description}
            onChange={handleChange}
          />
        </Card>
      </motion.div>
      {showInternalContinueButton && (
        <motion.div 
          className='flex justify-end pt-4'
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Button 
            onClick={handleContinue}
            disabled={updateBusinessMutation.isPending}
            className='bg-[#290BD9] hover:bg-[#2309C5]'
          >
            Continue
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
});
